import { categories, serviceProviderCategories, sidebarCategories } from "@/data/mockData";

export interface EquipmentCategoryConfig {
  name: string;
  subcategories: string[];
}

export interface MarketplaceCategoryConfig {
  equipment: EquipmentCategoryConfig[];
  services: string[];
}

const normalize = (value?: string) => String(value || "").trim();

const unique = (items: string[]) => {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = normalize(item).toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const defaultMarketplaceCategoryConfig: MarketplaceCategoryConfig = {
  equipment: sidebarCategories.map((item) => ({
    name: item.name,
    subcategories: unique(item.subcategories),
  })),
  services: unique(serviceProviderCategories),
};

const fallbackEquipmentName = categories[0]?.name || "Others";

export const normalizeMarketplaceCategoryConfig = (
  config?: Partial<MarketplaceCategoryConfig> | null,
): MarketplaceCategoryConfig => {
  const equipment = Array.isArray(config?.equipment)
    ? config!.equipment
      .map((item) => ({
        name: normalize(item?.name),
        subcategories: unique(Array.isArray(item?.subcategories) ? item.subcategories.map((entry) => normalize(entry)) : []),
      }))
      .filter((item) => item.name)
    : [];

  const services = Array.isArray(config?.services)
    ? unique(config!.services.map((item) => normalize(item)))
    : [];

  return {
    equipment: equipment.length > 0 ? equipment : defaultMarketplaceCategoryConfig.equipment,
    services: services.length > 0 ? services : defaultMarketplaceCategoryConfig.services,
  };
};

export const readMarketplaceCategoryConfigFromStorage = (): MarketplaceCategoryConfig => {
  if (typeof window === "undefined") return defaultMarketplaceCategoryConfig;

  try {
    const raw = window.localStorage.getItem("elra_platform_settings");
    if (!raw) return defaultMarketplaceCategoryConfig;

    const parsed = JSON.parse(raw) as { marketplaceCategories?: Partial<MarketplaceCategoryConfig> };
    return normalizeMarketplaceCategoryConfig(parsed.marketplaceCategories);
  } catch {
    return defaultMarketplaceCategoryConfig;
  }
};

export const resolveMarketplaceCategoryConfig = (
  config?: Partial<MarketplaceCategoryConfig> | null,
): MarketplaceCategoryConfig => {
  if (config) return normalizeMarketplaceCategoryConfig(config);
  return readMarketplaceCategoryConfigFromStorage();
};

export const getFallbackEquipmentCategoryName = () => fallbackEquipmentName;
